import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, DollarSign, Users, Package, Calendar, TrendingUp, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const InternalActivityStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // 'all', '30days', '90days', '1year', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    fetchStatistics();
  }, [dateFilter, customStartDate, customEndDate]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      let params = {};
      
      // Apply date filters
      if (dateFilter === '30days') {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        params.start_date = startDate.toISOString();
      } else if (dateFilter === '90days') {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);
        params.start_date = startDate.toISOString();
      } else if (dateFilter === '1year') {
        const startDate = new Date();
        startDate.setFullYear(startDate.getFullYear() - 1);
        params.start_date = startDate.toISOString();
      } else if (dateFilter === 'custom' && customStartDate) {
        params.start_date = new Date(customStartDate).toISOString();
        if (customEndDate) {
          params.end_date = new Date(customEndDate).toISOString();
        }
      }
      
      const response = await axios.get(`${API_BASE}/api/admin/internal-activity-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setStatistics(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching internal activity statistics:', err);
      setError('Kunne ikke hente statistikk for interne aktiviteter');
      setLoading(false);
    }
  };

  // Get activity display name
  const getActivityDisplayName = (tag) => {
    const names = {
      'GJESTE-SKYTING': 'Gjeste-skyting',
      'SIKKERH.KURS': 'Sikkerhetskurs',
      'INNSKYTING': 'Innskyting',
      'KLUBBSTEVNE': 'Klubbstevne'
    };
    return names[tag] || tag;
  };

  // Get activity icon background color
  const getActivityColor = (tag) => {
    const colors = {
      'GJESTE-SKYTING': 'bg-blue-500',
      'SIKKERH.KURS': 'bg-green-500',
      'INNSKYTING': 'bg-orange-500',
      'KLUBBSTEVNE': 'bg-purple-500'
    };
    return colors[tag] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6" data-testid="internal-activity-stats-loading">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="h-6 w-6 text-green-400" />
          <h3 className="text-xl font-bold text-white">Interne aktiviteter - Inntekter</h3>
        </div>
        <p className="text-gray-400 text-center py-8">Laster statistikk...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6" data-testid="internal-activity-stats-error">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="h-6 w-6 text-red-400" />
          <h3 className="text-xl font-bold text-white">Interne aktiviteter - Inntekter</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchStatistics}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="bg-gray-800 rounded-lg p-6" data-testid="internal-activity-stats-empty">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="h-6 w-6 text-green-400" />
          <h3 className="text-xl font-bold text-white">Interne aktiviteter - Inntekter</h3>
        </div>
        <p className="text-gray-400 text-center py-8">Ingen data tilgjengelig</p>
      </div>
    );
  }

  const { stats_by_activity, total_activity_revenue, total_ammo_revenue, total_revenue, total_participants, internal_prices } = statistics;

  // Check if there's any data
  const hasData = stats_by_activity.some(s => s.count > 0 || s.activity_revenue > 0);

  return (
    <div className="space-y-6" data-testid="internal-activity-stats">
      {/* Header with Date Filter */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-green-400" />
            <h3 className="text-xl font-bold text-white">Interne aktiviteter - Inntekter</h3>
          </div>
          
          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              data-testid="date-filter-select"
            >
              <option value="all">Alle tider</option>
              <option value="30days">Siste 30 dager</option>
              <option value="90days">Siste 90 dager</option>
              <option value="1year">Siste år</option>
              <option value="custom">Egendefinert</option>
            </select>
            
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  placeholder="Fra dato"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                  placeholder="Til dato"
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-gray-700 rounded-lg" data-testid="total-revenue-card">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <DollarSign className="h-4 w-4 text-green-400" />
              <span>Total inntekt</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{total_revenue.toLocaleString()} kr</p>
          </div>
          
          <div className="p-4 bg-gray-700 rounded-lg" data-testid="activity-revenue-card">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Activity className="h-4 w-4 text-blue-400" />
              <span>Aktivitetsinntekt</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{total_activity_revenue.toLocaleString()} kr</p>
            <p className="text-xs text-gray-500 mt-1">Fra kursavgifter og lignende</p>
          </div>
          
          <div className="p-4 bg-gray-700 rounded-lg" data-testid="ammo-revenue-card">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Package className="h-4 w-4 text-yellow-400" />
              <span>Ammo-inntekt</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{total_ammo_revenue.toLocaleString()} kr</p>
            <p className="text-xs text-gray-500 mt-1">Fra ammunisjonssalg</p>
          </div>
          
          <div className="p-4 bg-gray-700 rounded-lg" data-testid="participants-card">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Users className="h-4 w-4 text-purple-400" />
              <span>Totalt deltakere</span>
            </div>
            <p className="text-2xl font-bold text-white">{total_participants}</p>
          </div>
        </div>

        {/* Info about configuration */}
        <div className="p-3 bg-blue-900/20 border border-blue-700 rounded-lg mb-6">
          <p className="text-sm text-blue-200">
            <strong>Konfigurasjon:</strong> Prisene for interne aktiviteter kan settes under Innstillinger &gt; Ammunisjon &gt; "Inntekter for Interne aktiviteter".
          </p>
        </div>

        {/* Stats by Activity */}
        {!hasData ? (
          <div className="text-center py-8">
            <Activity className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Ingen interne aktiviteter registrert i valgt periode</p>
            <p className="text-sm text-gray-500 mt-2">
              Gjeste-skytinger, sikkerhetskurs og andre interne aktiviteter vil vises her
            </p>
          </div>
        ) : (
          <div>
            <h4 className="text-white font-semibold mb-4">Inntekter per aktivitetstype</h4>
            <div className="space-y-4">
              {stats_by_activity.map((stat) => (
                <div key={stat.tag} className="bg-gray-700 rounded-lg p-4" data-testid={`activity-stat-${stat.tag.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${getActivityColor(stat.tag)} rounded-lg flex items-center justify-center`}>
                        <Activity className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h5 className="text-white font-medium text-lg">{getActivityDisplayName(stat.tag)}</h5>
                        <p className="text-xs text-gray-400">
                          Konfiguert pris: {internal_prices[stat.tag] || 0} kr per aktivitet
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-green-400 font-bold text-xl">{stat.total_revenue.toLocaleString()} kr</span>
                      <p className="text-xs text-gray-400">Total inntekt</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-gray-600">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Antall salg</p>
                      <p className="text-white font-semibold">{stat.count} stk</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Ammo (esker)</p>
                      <p className="text-white font-semibold">{stat.ammo_boxes} stk</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Aktivitetsinntekt</p>
                      <p className="text-blue-400 font-semibold">{stat.activity_revenue.toLocaleString()} kr</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Ammo-inntekt</p>
                      <p className="text-yellow-400 font-semibold">{stat.ammo_revenue.toLocaleString()} kr</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Configured Prices Overview */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-400" />
          Konfigurerte priser
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(internal_prices).map(([tag, price]) => (
            <div key={tag} className="p-3 bg-gray-700 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">{getActivityDisplayName(tag)}</p>
              <p className="text-lg font-bold text-white">{price} kr</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-4">
          Endre priser under Innstillinger &gt; Ammunisjon &gt; "Inntekter for Interne aktiviteter"
        </p>
      </div>
    </div>
  );
};

export default InternalActivityStatistics;
